import {
  Box,
  Typography,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  VolumeOff as SilenceIcon,
} from '@mui/icons-material';
import { AlertDetailModalProps, CARD_BORDER_RADIUS } from '../types';

/**
 * AlertDetailModal - Displays detailed information about a single alert.
 *
 * Features:
 * - Colored header based on severity (critical=red, warning=orange)
 * - Summary and description
 * - Timeline information (created, updated, started, ended)
 * - Alert labels as chips
 * - Annotations display
 * - Action buttons (silence, acknowledge)
 */
export function AlertDetailModal({ alert, open, onClose, onSilence }: AlertDetailModalProps) {
  if (!alert) return null;

  const headerBgColor = alert.severity === 'critical' ? 'error.main' : 'warning.main';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: CARD_BORDER_RADIUS,
          maxHeight: '80vh',
        },
      }}
    >
      {/* Alert Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: headerBgColor,
          color: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {alert.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip
                label={alert.severity.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  height: 22,
                }}
              />
              <Chip
                label={alert.status === 'active' ? 'Active' : 'Resolved'}
                size="small"
                sx={{
                  bgcolor:
                    alert.status === 'active' ? 'error.dark' : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  height: 22,
                }}
              />
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Alert Content */}
      <DialogContent sx={{ p: 2 }}>
        {/* Summary */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Summary
          </Typography>
          <Typography variant="body1">{alert.summary}</Typography>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {alert.description}
          </Typography>
        </Box>

        {/* Timeline */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Timeline
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">{alert.timeline.created}</Typography>
              <Typography variant="caption" color="text.secondary">
                ({alert.timeline.createdAgo})
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body2">{alert.timeline.updated}</Typography>
              <Typography variant="caption" color="text.secondary">
                ({alert.timeline.updatedAgo})
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Started
              </Typography>
              <Typography variant="body2">{alert.timeline.started}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Ended
              </Typography>
              <Typography variant="body2">{alert.timeline.ended || '—'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Labels */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Labels
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {Object.entries(alert.labels).map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        </Box>

        {/* Annotations */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Annotations
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {Object.entries(alert.annotations).map(([key, value]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {key}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {onSilence && alert.status === 'active' && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<SilenceIcon />}
              onClick={() => onSilence(alert.labels)}
              sx={{ flex: 1 }}
            >
              Silence Alert
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AlertDetailModal;
